import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';

const CALL_TYPES = [
  { value: 'tax_office', icon: '🏛', label: 'Urząd Skarbowy' },
  { value: 'zus', icon: '🏥', label: 'ZUS' },
  { value: 'client', icon: '👤', label: 'Klient' },
  { value: 'custom', icon: '📞', label: 'Inny' },
];

const LANGUAGES = [
  { value: 'pl', label: '🇵🇱 PL' },
  { value: 'en', label: '🇬🇧 EN' },
  { value: 'uk', label: '🇺🇦 UK' },
  { value: 'ru', label: '🇷🇺 RU' },
  { value: 'sv', label: '🇸🇪 SV' },
  { value: 'de', label: '🇩🇪 DE' },
  { value: 'es', label: '🇪🇸 ES' },
  { value: 'fr', label: '🇫🇷 FR' },
  { value: 'it', label: '🇮🇹 IT' },
  { value: 'pt', label: '🇵🇹 PT' },
  { value: 'tr', label: '🇹🇷 TR' },
  { value: 'nl', label: '🇳🇱 NL' },
  { value: 'ja', label: '🇯🇵 JA' },
  { value: 'zh', label: '🇨🇳 ZH' },
  { value: 'ko', label: '🇰🇷 KO' },
  { value: 'ar', label: '🇸🇦 AR' },
  { value: 'hi', label: '🇮🇳 HI' },
];

const TEMPLATES = {
  zus: [
    { label: 'Status A1', prompt: 'Zapytaj o status zaświadczenia A1 dla pracownika {name}, PESEL {pesel}' },
    { label: 'Zaległości ZUS', prompt: 'Zapytaj o ewentualne zaległości w składkach ZUS dla firmy NIP {nip}' },
  ],
  tax_office: [
    { label: 'Termin VAT', prompt: 'Zapytaj o termin złożenia deklaracji VAT za {month}' },
    { label: 'Status zwrotu', prompt: 'Zapytaj o status zwrotu VAT za {period} dla NIP {nip}' },
  ],
  client: [
    { label: 'Przypomnienie o fakturze', prompt: 'Przypomnij o niezapłaconej fakturze nr {number} na kwotę {amount} PLN' },
    { label: 'Potwierdzenie spotkania', prompt: 'Potwierdź spotkanie zaplanowane na {date} o godzinie {time}' },
  ],
};

const STATUS_MAP = {
  pending: { icon: '⏳', label: 'Oczekuje', color: '#ffa726' },
  calling: { icon: '📞', label: 'Rozmowa', color: '#42a5f5' },
  completed: { icon: '✅', label: 'Zakończone', color: '#66bb6a' },
  failed: { icon: '❌', label: 'Błąd', color: '#ef5350' },
};

export default function AiCalls() {
  const { currentCompany, setPage } = useApp();
  const [tab, setTab] = useState('new');
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  // Form state
  const [phone, setPhone] = useState('+48');
  const [callType, setCallType] = useState('custom');
  const [prompt, setPrompt] = useState('');
  const [language, setLanguage] = useState('pl');
  const [activeCallId, setActiveCallId] = useState(null);

  // Voice input state
  const [recording, setRecording] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // AI prompt enhancement state
  const [enhancing, setEnhancing] = useState(false);
  const [showEnhanced, setShowEnhanced] = useState(false);
  const [enhancedPrompt, setEnhancedPrompt] = useState('');

  useEffect(() => {
    if (currentCompany) loadCalls();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [currentCompany]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (activeCallId) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/ai-calls/${activeCallId}`);
          setSelectedCall(data);
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setActiveCallId(null);
            loadCalls();
          }
        } catch {}
      }, 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeCallId]);

  async function loadCalls() {
    try {
      const { data } = await api.get(`/ai-calls?company_id=${currentCompany.id}`);
      setCalls(data);
    } catch {}
  }

  // ─── Voice recording ───
  async function startVoiceRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 500) return;
        setVoiceProcessing(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'voice.webm');
          const { data } = await api.post('/voice/stt', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
          });
          if (data.text) {
            setPrompt(prev => prev ? prev + ' ' + data.text : data.text);
          }
        } catch (e) {
          setError('Nie udało się rozpoznać mowy. Spróbuj ponownie.');
        }
        setVoiceProcessing(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setTimeout(() => { if (recorder.state === 'recording') { recorder.stop(); setRecording(false); } }, 60000);
    } catch (e) {
      setError('Brak dostępu do mikrofonu');
    }
  }

  function stopVoiceRecording() {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }

  // ─── AI prompt enhancement ───
  async function enhancePrompt() {
    if (!prompt.trim()) { setError('Najpierw opisz cel rozmowy'); return; }
    setEnhancing(true);
    setError('');
    try {
      const { data } = await api.post('/ai-calls/enhance-prompt', {
        raw_prompt: prompt,
        call_type: callType,
        language,
      });
      setEnhancedPrompt(data.enhanced_prompt);
      setShowEnhanced(true);
    } catch (e) {
      setError('Nie udało się ulepszyć promptu');
    }
    setEnhancing(false);
  }

  function acceptEnhanced() {
    setPrompt(enhancedPrompt);
    setShowEnhanced(false);
    setEnhancedPrompt('');
  }

  async function initiateCall() {
    if (!phone || phone.length < 8) { setError('Podaj prawidłowy numer telefonu'); return; }
    if (!prompt.trim()) { setError('Opisz cel rozmowy'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/ai-calls/initiate', {
        phone_number: phone,
        call_type: callType,
        prompt,
        language,
        company_id: currentCompany.id,
      });
      setActiveCallId(data.id);
      setSelectedCall({ id: data.id, status: 'calling', phone_number: phone, prompt, transcript: '' });
      setTab('detail');
      setPrompt('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Nie udało się zadzwonić');
    } finally {
      setLoading(false);
    }
  }

  async function hangup() {
    if (!activeCallId) return;
    try {
      await api.post(`/ai-calls/${activeCallId}/hangup`);
      setActiveCallId(null);
      loadCalls();
    } catch {}
  }

  function openCall(call) {
    setSelectedCall(call);
    setTab('detail');
    if (call.status === 'calling') setActiveCallId(call.id);
  }

  function applyTemplate(tpl) {
    setPrompt(tpl.prompt);
  }

  if (!currentCompany) {
    return (
      <div style={styles.container}>
        <BackButton onClick={() => setPage('dashboard')} />
        <p style={{ color: '#aaa', textAlign: 'center', marginTop: 40 }}>Wybierz firmę, aby korzystać z AI rozmów</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <BackButton onClick={() => tab === 'new' ? setPage('dashboard') : setTab('new')} />
      <h2 style={styles.title}>📞 AI Rozmowy</h2>

      <div style={styles.tabs}>
        <button style={tab === 'new' ? styles.tabActive : styles.tab} onClick={() => setTab('new')}>Nowa rozmowa</button>
        <button style={tab === 'history' ? styles.tabActive : styles.tab} onClick={() => { setTab('history'); loadCalls(); }}>Historia</button>
      </div>

      {tab === 'new' && (
        <div style={styles.form}>
          {/* Call type */}
          <label style={styles.label}>Typ rozmowy</label>
          <div style={styles.typeGrid}>
            {CALL_TYPES.map(ct => (
              <button key={ct.value} style={callType === ct.value ? styles.typeActive : styles.typeBtn} onClick={() => setCallType(ct.value)}>
                <span style={{ fontSize: 24 }}>{ct.icon}</span>
                <span style={{ fontSize: 12 }}>{ct.label}</span>
              </button>
            ))}
          </div>

          {/* Phone */}
          <label style={styles.label}>Numer telefonu</label>
          <input style={styles.input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+48 123 456 789" />

          {/* Language */}
          <label style={styles.label}>Język rozmowy</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {LANGUAGES.map(l => (
              <button key={l.value} style={language === l.value ? styles.langActive : styles.langBtn} onClick={() => setLanguage(l.value)}>{l.label}</button>
            ))}
          </div>

          {/* Templates */}
          {TEMPLATES[callType] && (
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Szablony</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TEMPLATES[callType].map((tpl, i) => (
                  <button key={i} style={styles.tplBtn} onClick={() => applyTemplate(tpl)}>{tpl.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Prompt with voice input */}
          <label style={styles.label}>Cel rozmowy / co zapytać</label>
          <div style={{ position: 'relative' }}>
            <textarea
              style={{ ...styles.textarea, paddingRight: 56 }}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Opisz czego AI ma się dowiedzieć... lub naciśnij 🎙 żeby podyktować"
              rows={4}
            />
            {/* Voice button */}
            <button
              style={{
                position: 'absolute',
                right: 8,
                bottom: 24,
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                fontSize: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: recording ? 'linear-gradient(135deg, #e53935, #ef5350)' : 'linear-gradient(135deg, #42a5f5, #1e88e5)',
                color: '#fff',
                boxShadow: recording ? '0 0 20px rgba(229,57,53,0.5)' : '0 2px 8px rgba(0,0,0,0.3)',
                animation: recording ? 'pulse 1s infinite' : 'none',
              }}
              onClick={recording ? stopVoiceRecording : startVoiceRecording}
              disabled={voiceProcessing}
            >
              {voiceProcessing ? '⏳' : recording ? '⏹' : '🎙'}
            </button>
          </div>

          {voiceProcessing && (
            <div style={{ color: '#42a5f5', fontSize: 13, marginBottom: 8, textAlign: 'center' }}>
              ⏳ Rozpoznawanie mowy...
            </div>
          )}

          {recording && (
            <div style={{ color: '#ef5350', fontSize: 13, marginBottom: 8, textAlign: 'center', animation: 'pulse 1.5s infinite' }}>
              🔴 Nagrywanie... Kliknij ⏹ aby zakończyć
            </div>
          )}

          {/* AI Enhance button */}
          {prompt.trim() && (
            <button
              style={styles.enhanceBtn}
              onClick={enhancePrompt}
              disabled={enhancing}
            >
              {enhancing ? '⏳ AI analizuje...' : '✨ AI ulepsz prompt'}
            </button>
          )}

          {/* Enhanced prompt preview */}
          {showEnhanced && (
            <div style={styles.enhancedBox}>
              <label style={{ ...styles.label, color: '#66bb6a' }}>✨ Ulepszona wersja:</label>
              <p style={{ color: '#e0e0e0', fontSize: 14, lineHeight: 1.5, margin: '8px 0' }}>{enhancedPrompt}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={styles.acceptBtn} onClick={acceptEnhanced}>✅ Użyj</button>
                <button style={styles.rejectBtn} onClick={() => setShowEnhanced(false)}>❌ Zostaw oryginał</button>
              </div>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}

          <button style={styles.callBtn} onClick={initiateCall} disabled={loading}>
            {loading ? '⏳ Łączenie...' : '📞 Zadzwoń'}
          </button>
        </div>
      )}

      {tab === 'history' && (
        <div>
          {calls.length === 0 && <p style={{ color: '#aaa', textAlign: 'center', marginTop: 20 }}>Brak rozmów</p>}
          {calls.map(c => {
            const st = STATUS_MAP[c.status] || STATUS_MAP.pending;
            return (
              <div key={c.id} style={styles.callCard} onClick={() => openCall(c)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{c.phone_number}</span>
                  <span style={{ color: st.color, fontSize: 13 }}>{st.icon} {st.label}</span>
                </div>
                <div style={{ color: '#aaa', fontSize: 13, marginTop: 4 }}>
                  {CALL_TYPES.find(t => t.value === c.call_type)?.icon} {CALL_TYPES.find(t => t.value === c.call_type)?.label || c.call_type}
                  {c.duration_seconds ? ` · ${Math.floor(c.duration_seconds / 60)}:${String(c.duration_seconds % 60).padStart(2, '0')}` : ''}
                </div>
                {c.summary && <p style={{ color: '#ccc', fontSize: 13, marginTop: 6 }}>{c.summary.slice(0, 100)}...</p>}
                <div style={{ color: '#666', fontSize: 11, marginTop: 4 }}>{c.created_at?.slice(0, 16)}</div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'detail' && selectedCall && (
        <div>
          <div style={styles.detailHeader}>
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{selectedCall.phone_number}</span>
            <span style={{ color: (STATUS_MAP[selectedCall.status] || STATUS_MAP.pending).color }}>
              {(STATUS_MAP[selectedCall.status] || STATUS_MAP.pending).icon} {(STATUS_MAP[selectedCall.status] || STATUS_MAP.pending).label}
            </span>
          </div>

          {selectedCall.status === 'calling' && (
            <div style={styles.liveContainer}>
              <div style={styles.pulseOuter}>
                <div style={styles.pulseInner}>📞</div>
              </div>
              <p style={{ color: '#42a5f5', marginTop: 16 }}>Rozmowa w toku...</p>
              <button style={styles.hangupBtn} onClick={hangup}>🔴 Rozłącz</button>
            </div>
          )}

          {selectedCall.transcript && (
            <div style={styles.transcriptBox}>
              <label style={styles.label}>Transkrypcja</label>
              {selectedCall.transcript.split('\n').filter(Boolean).map((line, i) => {
                const isAi = line.startsWith('AI:');
                return (
                  <div key={i} style={{
                    padding: '8px 12px', marginBottom: 6, borderRadius: 12,
                    background: isAi ? 'rgba(66,165,245,0.15)' : 'rgba(255,255,255,0.08)',
                    color: isAi ? '#90caf9' : '#e0e0e0', fontSize: 14,
                  }}>{line}</div>
                );
              })}
            </div>
          )}

          {selectedCall.summary && (
            <div style={styles.summaryBox}>
              <label style={styles.label}>📋 Podsumowanie</label>
              <p style={{ color: '#e0e0e0', fontSize: 14, lineHeight: 1.5 }}>{selectedCall.summary}</p>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.3); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 600, margin: '0 auto' },
  title: { color: '#fff', textAlign: 'center', margin: '12px 0 16px' },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { flex: 1, padding: '10px 0', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, color: '#aaa', fontSize: 14, cursor: 'pointer' },
  tabActive: { flex: 1, padding: '10px 0', background: 'rgba(66,165,245,0.2)', border: '1px solid #42a5f5', borderRadius: 8, color: '#42a5f5', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  form: {},
  label: { display: 'block', color: '#aaa', fontSize: 13, marginBottom: 6, fontWeight: 500 },
  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 },
  typeBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 4px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 10, color: '#ccc', cursor: 'pointer' },
  typeActive: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 4px', background: 'rgba(66,165,245,0.2)', border: '1px solid #42a5f5', borderRadius: 10, color: '#42a5f5', cursor: 'pointer', fontWeight: 600 },
  input: { width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, color: '#fff', fontSize: 16, marginBottom: 16, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, color: '#fff', fontSize: 14, resize: 'vertical', marginBottom: 8, boxSizing: 'border-box', fontFamily: 'inherit' },
  langBtn: { padding: '8px 16px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, color: '#ccc', cursor: 'pointer', fontSize: 14 },
  langActive: { padding: '8px 16px', background: 'rgba(66,165,245,0.2)', border: '1px solid #42a5f5', borderRadius: 8, color: '#42a5f5', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  tplBtn: { padding: '6px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#90caf9', cursor: 'pointer', fontSize: 13 },
  enhanceBtn: { width: '100%', padding: '12px', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 12 },
  enhancedBox: { padding: 14, background: 'rgba(102,187,106,0.08)', borderRadius: 12, border: '1px solid rgba(102,187,106,0.2)', marginBottom: 12 },
  acceptBtn: { flex: 1, padding: '10px', background: 'rgba(102,187,106,0.2)', border: '1px solid #66bb6a', borderRadius: 8, color: '#66bb6a', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  rejectBtn: { flex: 1, padding: '10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#aaa', fontSize: 14, cursor: 'pointer' },
  callBtn: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #43a047, #66bb6a)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 18, fontWeight: 700, cursor: 'pointer', marginTop: 8 },
  callCard: { padding: 14, background: 'rgba(255,255,255,0.06)', borderRadius: 12, marginBottom: 10, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 16 },
  liveContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 0' },
  pulseOuter: { width: 80, height: 80, borderRadius: '50%', background: 'rgba(66,165,245,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 2s infinite' },
  pulseInner: { fontSize: 36 },
  hangupBtn: { marginTop: 20, padding: '12px 32px', background: 'linear-gradient(135deg, #e53935, #ef5350)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  transcriptBox: { padding: 14, background: 'rgba(255,255,255,0.04)', borderRadius: 12, marginBottom: 16, display: 'flex', flexDirection: 'column' },
  summaryBox: { padding: 14, background: 'rgba(102,187,106,0.1)', borderRadius: 12, border: '1px solid rgba(102,187,106,0.2)' },
  error: { color: '#ef5350', fontSize: 13, marginBottom: 8 },
};
